import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'isomorphic-dompurify';
import { Shield, Award, Users, Eye, Heart, Star, Sparkles, Globe, BookOpen, Briefcase, Compass, Flag, Download } from 'lucide-react';
import { absoluteUrl, api } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import HistorySection from './HistorySection';
import ContactForm from './ContactForm';
import BoardSection from './sections/BoardSection';
import PastPresidentsSection from './sections/PastPresidentsSection';
import PeriodicalsSection from './sections/PeriodicalsSection';
import logoWhite from '../assets/logo-white.png';

/**
 * Guess a lucide-react icon based on the title — used as a stylish fallback
 * when the CMS items don't have an explicit icon_url. Keeps the visual rhythm
 * of the cards block without forcing the admin to upload an icon per item.
 */
const TITLE_ICON_MAP = [
  { match: /etica|ética|integridad|honest/i, Icon: Shield },
  { match: /excelencia|calidad|profesional/i, Icon: Award },
  { match: /comunidad|colega|miembro|asociaci/i, Icon: Users },
  { match: /visibilidad|prestigio|reconoc/i, Icon: Eye },
  { match: /solidar|apoyo|amistad/i, Icon: Heart },
  { match: /innov|nuevo|futuro/i, Icon: Sparkles },
  { match: /internacional|global|mundo/i, Icon: Globe },
  { match: /forma|educa|cursos|libros/i, Icon: BookOpen },
  { match: /trabajo|carrera|profesi/i, Icon: Briefcase },
  { match: /historia|tradici/i, Icon: Flag },
  { match: /visión|vision|mision|misión|propósito/i, Icon: Compass },
];

function pickIcon(title) {
  if (!title) return Star;
  for (const { match, Icon } of TITLE_ICON_MAP) {
    if (match.test(title)) return Icon;
  }
  return Star;
}

/**
 * Editable — used inside the visual editor to enable inline text edits.
 * In view (non-edit) mode this is just a plain Tag with the value.
 */
function Editable({ value, onChange, as: Tag = 'span', className, placeholder, editMode, multiline }) {
  if (!editMode) {
    return <Tag className={className}>{value}</Tag>;
  }
  return (
    <Tag
      className={`${className || ''} outline-none focus:bg-cmic-50/40 focus:ring-2 focus:ring-cmic-400 rounded transition-colors`}
      contentEditable
      suppressContentEditableWarning
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        const next = (e.currentTarget.textContent || '').trim();
        if (next !== (value || '')) onChange?.(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
          e.currentTarget.textContent = value || '';
          e.currentTarget.blur();
        }
      }}
      data-placeholder={placeholder}
      data-testid={`editable-${Tag}`}
    >
      {value || ''}
    </Tag>
  );
}

/**
 * BlockRenderer — renders a single CMS block based on its `kind`.
 * Supported kinds (phase-1):
 *   • hero       — { label, title_es, title_en, subtitle_es, subtitle_en, image_url, cta_text_es, cta_text_en, cta_url }
 *   • text       — { html_es, html_en, max_width }
 *   • image      — { url, alt_es, alt_en, caption_es, caption_en, full_width }
 *   • cta        — { title_es, title_en, body_es, body_en, button_text_es, button_text_en, button_url, variant }
 *   • cards      — { title_es, title_en, items: [{ title, body, icon_url, link_url }] }
 *   • stats      — { items: [{ value, label_es, label_en }] }
 *   • spacer     — { height }   // pixels
 *   • html       — { html_es, html_en } // raw escape hatch
 */

function pick(block, key, locale) {
  return block.data?.[`${key}_${locale}`] ?? block.data?.[`${key}_es`] ?? '';
}

const SAFE = (html) => DOMPurify.sanitize(html || '');

// Decide whether a user-entered URL points to a file/external resource (open in
// a new tab via <a>) or an internal SPA route (<Link>). React Router's <Link>
// would otherwise treat absolute URLs / file paths as internal routes and bounce
// the user to the home page (catch-all).
function isFileOrExternal(url) {
  if (!url) return false;
  if (/^(https?:)?\/\//i.test(url)) return true;       // http(s):// or //
  if (/^(mailto:|tel:)/i.test(url)) return true;
  if (/^\/(uploads|api)\b/i.test(url)) return true;     // backend-served files
  if (/\.(pdf|docx?|xlsx?|pptx?|zip|rar|csv|txt|jpe?g|png|gif|webp|svg|mp4|mp3|wav)(\?.*)?$/i.test(url)) return true;
  return false;
}

// Smart link used by hero/cta buttons. Files & external URLs open in a new tab;
// internal routes use the SPA router.
function BlockLink({ url, className, style, children }) {
  if (isFileOrExternal(url)) {
    const download = /\.(pdf|docx?|xlsx?|pptx?|zip|rar|csv|txt)(\?.*)?$/i.test(url);
    return (
      <a href={absoluteUrl(url)} target="_blank" rel="noopener noreferrer" {...(download ? { download: true } : {})} className={className} style={style}>
        {children}
      </a>
    );
  }
  return <Link to={url || '/es'} className={className} style={style}>{children}</Link>;
}

export default function BlockRenderer({ block, locale = 'es', editMode }) {
  if (!block) return null;
  const k = block.kind;
  const d = block.data || {};
  const fieldKey = (base) => `${base}_${locale}`;
  const onEdit = editMode ? (key, value) => editMode(block.id, key, value) : null;

  if (k === 'hero') {
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="hero" className="relative overflow-hidden bg-gradient-to-br from-cmic-700 via-cmic-600 to-cmic-800" style={{ background: `linear-gradient(135deg, var(--cmic-primary, #004B7A) 0%, var(--cmic-primary, #006199) 60%, color-mix(in srgb, var(--cmic-primary, #00355C) 80%, black) 100%)` }}>
        {d.image_url && (
          <div className="absolute inset-0 opacity-15">
            <img src={absoluteUrl(d.image_url)} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className={block.page_key === 'home' ? 'flex flex-col lg:flex-row lg:items-center gap-7 lg:gap-12' : ''}>
            {block.page_key === 'home' && (
              <img src={logoWhite} alt="CMIC" data-testid="hero-logo" className="h-24 sm:h-28 w-auto flex-shrink-0 drop-shadow-md" />
            )}
            {block.page_key === 'home' && (
              <div className="hidden lg:block w-px self-stretch bg-white/25" aria-hidden="true" />
            )}
            <div className="min-w-0 max-w-2xl">
              {(pick(block, 'label', locale) || editMode) && (
                <Editable
                  as="span"
                  editMode={!!onEdit}
                  value={pick(block, 'label', locale)}
                  onChange={(v) => onEdit(fieldKey('label'), v)}
                  placeholder="Etiqueta opcional"
                  className="inline-flex items-center text-xs font-semibold tracking-widest uppercase text-cmic-100 bg-cmic-500/40 backdrop-blur px-3 py-1.5 rounded-full mb-5"
                />
              )}
              {(block.page_key !== 'home' || editMode) && (
                <Editable
                  as="h1"
                  editMode={!!onEdit}
                  value={pick(block, 'title', locale)}
                  onChange={(v) => onEdit(fieldKey('title'), v)}
                  placeholder="Título del hero"
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl"
                />
              )}
              {(pick(block, 'subtitle', locale) || editMode) && (
                <Editable
                  as="p"
                  editMode={!!onEdit}
                  value={pick(block, 'subtitle', locale)}
                  onChange={(v) => onEdit(fieldKey('subtitle'), v)}
                  placeholder="Subtítulo opcional"
                  multiline
                  className="mt-6 text-lg sm:text-xl text-cmic-50 max-w-2xl leading-relaxed"
                />
              )}
              <div className="flex flex-wrap items-center gap-3 mt-8">
                {(pick(block, 'cta_text', locale) && d.cta_url) && (
                  editMode ? (
                    <span className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-xl font-semibold shadow-md" style={{ color: 'var(--cmic-primary, #004B7A)' }}>
                      {pick(block, 'cta_text', locale)} <span>→</span>
                    </span>
                  ) : (
                    <BlockLink url={d.cta_url} className="inline-flex items-center gap-2 bg-white hover:bg-cmic-50 px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg" style={{ color: 'var(--cmic-primary, #004B7A)' }}>
                      {pick(block, 'cta_text', locale)}
                      <span>→</span>
                    </BlockLink>
                  )
                )}
                {block.page_key === 'home' && (
                  <Link
                    to={locale === 'en' ? '/en/membership' : '/es/membresia'}
                    data-testid="hero-membership-btn"
                    className="inline-flex items-center gap-2 bg-white hover:bg-cmic-50 px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                    style={{ color: 'var(--cmic-primary, #004B7A)' }}
                  >
                    {locale === 'en' ? 'Become a Member' : 'Hazte Miembro'}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (k === 'text') {
    const html = pick(block, 'html', locale);
    const maxW = d.max_width || '3xl';
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="text" className="py-12 sm:py-16 bg-white">
        <div className={`max-w-${maxW} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div lang="es" className="prose prose-lg max-w-none break-words [hyphens:auto] prose-headings:text-charcoal-800 prose-a:text-cmic-600 prose-strong:text-charcoal-800" dangerouslySetInnerHTML={{ __html: SAFE(normalizeHtml(html)) }} />
        </div>
      </section>
    );
  }

  if (k === 'image') {
    if (!d.url) return null;
    const alt = pick(block, 'alt', locale);
    const caption = pick(block, 'caption', locale);
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="image" className="py-8 bg-white">
        <div className={`${d.full_width ? 'w-full' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'}`}>
          <figure>
            <img src={absoluteUrl(d.url)} alt={alt} className="w-full h-auto rounded-2xl shadow-sm" />
            {caption && <figcaption className="mt-3 text-sm text-charcoal-500 text-center italic">{caption}</figcaption>}
          </figure>
        </div>
      </section>
    );
  }

  if (k === 'cta') {
    const title = pick(block, 'title', locale);
    const body = pick(block, 'body', locale);
    const btn = pick(block, 'button_text', locale);
    const isDark = d.variant === 'dark';
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="cta" className={`py-16 ${isDark ? 'bg-charcoal-800 text-white' : 'bg-cmic-50'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {(title || editMode) && (
            <Editable
              as="h2"
              editMode={!!onEdit}
              value={title}
              onChange={(v) => onEdit(fieldKey('title'), v)}
              placeholder="Título de la llamada a la acción"
              className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-charcoal-800'} block`}
            />
          )}
          {(body || editMode) && (
            <Editable
              as="p"
              editMode={!!onEdit}
              value={body}
              onChange={(v) => onEdit(fieldKey('body'), v)}
              placeholder="Descripción (opcional)"
              multiline
              className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-charcoal-100' : 'text-charcoal-600'} block`}
            />
          )}
          {(btn && d.button_url) && (
            editMode ? (
              <span className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md ${isDark ? 'bg-white' : 'text-white'}`} style={isDark ? { color: 'var(--cmic-primary, #004B7A)' } : { backgroundColor: 'var(--cmic-primary, #006199)' }}>
                {btn} <span>→</span>
              </span>
            ) : (
              <BlockLink url={d.button_url} className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg ${isDark ? 'bg-white hover:bg-cmic-50' : 'text-white'}`} style={isDark ? { color: 'var(--cmic-primary, #004B7A)' } : { backgroundColor: 'var(--cmic-primary, #006199)' }}>
                {btn}
                <span>→</span>
              </BlockLink>
            )
          )}
        </div>
      </section>
    );
  }

  if (k === 'cards') {
    const items = Array.isArray(d.items) ? d.items : [];
    const title = pick(block, 'title', locale);
    // Grid columns adaptive by item count so we never leave orphan items in the last row.
    const gridCols = items.length === 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : items.length === 2
      ? 'sm:grid-cols-2'
      : items.length === 1
      ? 'max-w-md mx-auto'
      : 'sm:grid-cols-2 lg:grid-cols-3';
    // Soft color palette rotated across cards so they feel intentional even without uploaded icons.
    const accents = ['bg-cmic-50 text-cmic-600', 'bg-amber-50 text-amber-600', 'bg-emerald-50 text-emerald-600', 'bg-indigo-50 text-indigo-600', 'bg-rose-50 text-rose-600', 'bg-sky-50 text-sky-600'];
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="cards" className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && (
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-charcoal-900">{title}</h2>
              <div className="mt-3 h-1 w-16 bg-cmic-500 rounded-full mx-auto" />
            </div>
          )}
          <div className={`grid gap-6 ${gridCols}`}>
            {items.map((it, i) => {
              const itTitle = it[`title_${locale}`] || it.title_es || it.title || '';
              const itBody = it[`body_${locale}`] || it.body_es || it.body || '';
              const FallbackIcon = pickIcon(itTitle);
              const accent = accents[i % accents.length];
              const rawLink = it.link_url || '';
              const href = it.file_url ? absoluteUrl(it.file_url) : (isFileOrExternal(rawLink) ? absoluteUrl(rawLink) : rawLink);
              const isDownload = !!it.file_url;
              const isExternal = isDownload || isFileOrExternal(rawLink);
              const cardClass = "group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center sm:text-left block";
              const inner = (
                <>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4 ${accent}`}>
                    {it.icon_url
                      ? <img src={absoluteUrl(it.icon_url)} alt="" className="w-8 h-8 object-contain" />
                      : <FallbackIcon size={26} strokeWidth={2} />}
                  </div>
                  {itTitle && <h3 className="font-bold text-charcoal-900 mb-2 text-lg">{itTitle}</h3>}
                  {itBody && <p className="text-sm text-charcoal-600 leading-relaxed">{itBody}</p>}
                  {href && (
                    <span className="inline-flex items-center gap-1 mt-3 text-cmic-600 group-hover:text-cmic-700 font-medium text-sm">
                      {isDownload
                        ? <>{locale === 'en' ? 'Download' : 'Descargar'} <Download size={15} className="transition-transform group-hover:translate-y-0.5" /></>
                        : <>{locale === 'en' ? 'Learn more' : 'Saber más'} <span className="transition-transform group-hover:translate-x-0.5">→</span></>}
                    </span>
                  )}
                </>
              );
              if (!href) return <div key={i} className={cardClass}>{inner}</div>;
              if (isDownload) return <a key={i} href={href} download={it.file_name || true} target="_blank" rel="noopener noreferrer" className={cardClass}>{inner}</a>;
              if (isExternal) return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={cardClass}>{inner}</a>;
              return <Link key={i} to={href} className={cardClass}>{inner}</Link>;
            })}
          </div>
        </div>
      </section>
    );
  }

  if (k === 'stats') {
    const items = Array.isArray(d.items) ? d.items : [];
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="stats" className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-6 ${items.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : items.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {items.map((it, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--cmic-primary, #006199)' }}>{it.value}</div>
                <div className="text-sm text-charcoal-500 mt-1 uppercase tracking-wider">{it[`label_${locale}`] || it.label_es || it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (k === 'gallery') {
    const items = Array.isArray(d.items) ? d.items : [];
    const title = pick(block, 'title', locale);
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="gallery" className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-8">{title}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((it, i) => (
              <a key={i} href={absoluteUrl(it.url)} target="_blank" rel="noopener noreferrer" className="block group">
                <div className="relative aspect-square overflow-hidden rounded-xl bg-gray-100">
                  <img src={absoluteUrl(it.url)} alt={it.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                {it.caption && <p className="text-xs text-charcoal-500 mt-1.5 text-center">{it.caption}</p>}
              </a>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (k === 'video') {
    if (!d.url) return null;
    // Extract YouTube/Vimeo IDs
    let embed = d.url;
    const yt = d.url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^?&"'>\s]+)/);
    const vi = d.url.match(/vimeo\.com\/(\d+)/);
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    else if (vi) embed = `https://player.vimeo.com/video/${vi[1]}`;
    const title = pick(block, 'title', locale);
    const caption = pick(block, 'caption', locale);
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="video" className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-6">{title}</h2>}
          <div className="aspect-video rounded-2xl overflow-hidden shadow-md bg-charcoal-900">
            <iframe src={embed} title={title || 'Video'} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          {caption && <p className="text-sm text-charcoal-500 text-center mt-3 italic">{caption}</p>}
        </div>
      </section>
    );
  }

  if (k === 'accordion') {
    const items = Array.isArray(d.items) ? d.items : [];
    const title = pick(block, 'title', locale);
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="accordion" className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-8">{title}</h2>}
          <div className="space-y-3">
            {items.map((it, i) => {
              const q = it[`q_${locale}`] || it.q_es || '';
              const a = it[`a_${locale}`] || it.a_es || '';
              return (
                <details key={i} className="group bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3">
                    <span className="font-semibold text-charcoal-800">{q}</span>
                    <span className="text-cmic-600 text-xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-charcoal-600 leading-relaxed whitespace-pre-wrap">{a}</div>
                </details>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (k === 'testimonials') {
    const items = Array.isArray(d.items) ? d.items : [];
    const title = pick(block, 'title', locale);
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="testimonials" className="py-16 bg-gradient-to-br from-cmic-50 to-amber-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-10">{title}</h2>}
          <div className={`grid gap-6 ${items.length >= 3 ? 'md:grid-cols-3' : items.length === 2 ? 'md:grid-cols-2' : 'max-w-2xl mx-auto'}`}>
            {items.map((it, i) => (
              <figure key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 relative">
                <span className="absolute -top-3 left-5 bg-cmic-600 text-white rounded-full w-9 h-9 flex items-center justify-center text-xl font-serif">&ldquo;</span>
                <blockquote className="text-charcoal-700 leading-relaxed text-sm pt-3">{it.quote}</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3">
                  {it.photo_url ? <img src={absoluteUrl(it.photo_url)} alt={it.author} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-cmic-100 text-cmic-700 flex items-center justify-center font-bold text-sm">{(it.author || '?').charAt(0)}</div>}
                  <span className="text-xs font-medium text-charcoal-600">{it.author}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (k === 'spacer') {
    const h = parseInt(d.height, 10) || 40;
    return <div data-testid={`block-${block.id}`} data-block-kind="spacer" style={{ height: `${h}px` }} />;
  }

  if (k === 'map') {
    if (!d.embed_url && !(d.latitude && d.longitude)) return null;
    const title = pick(block, 'title', locale);
    const caption = pick(block, 'caption', locale);
    // If an embed_url is supplied (Google Maps "share→embed" iframe src), use it directly.
    // Otherwise build an OpenStreetMap embed from lat/lng.
    let src = d.embed_url || '';
    if (!src && d.latitude && d.longitude) {
      const lat = parseFloat(d.latitude);
      const lng = parseFloat(d.longitude);
      const zoom = parseFloat(d.zoom || 14);
      const dLat = 0.01 / Math.max(zoom / 14, 0.5);
      const dLng = 0.015 / Math.max(zoom / 14, 0.5);
      const bbox = `${lng - dLng},${lat - dLat},${lng + dLng},${lat + dLat}`;
      src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
    }
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="map" className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-6">{title}</h2>}
          <div className="aspect-video rounded-2xl overflow-hidden shadow-md border border-gray-200 bg-gray-100">
            <iframe src={src} title={title || 'Mapa'} className="w-full h-full" frameBorder="0" loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
          </div>
          {caption && <p className="text-sm text-charcoal-500 text-center mt-3 italic">{caption}</p>}
        </div>
      </section>
    );
  }

  if (k === 'contact_form') {
    return <ContactFormBlock block={block} locale={locale} />;
  }

  if (k === 'board_members') {
    return (
      <div data-testid={`block-${block.id}`} data-block-kind="board_members">
        <BoardSection />
      </div>
    );
  }

  if (k === 'past_presidents') {
    return (
      <div data-testid={`block-${block.id}`} data-block-kind="past_presidents">
        <PastPresidentsSection />
      </div>
    );
  }

  if (k === 'periodicals_list') {
    return (
      <div data-testid={`block-${block.id}`} data-block-kind="periodicals_list">
        <PeriodicalsSection />
      </div>
    );
  }

  if (k === 'embedded_member_list') {
    return <EmbeddedMemberListBlock block={block} locale={locale} />;
  }

  if (k === 'html') {
    return (
      <section data-testid={`block-${block.id}`} data-block-kind="html" className="py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" dangerouslySetInnerHTML={{ __html: SAFE(pick(block, 'html', locale)) }} />
      </section>
    );
  }

  if (k === 'president_message') {
    return <PresidentMessageBlock block={block} locale={locale} />;
  }

  if (k === 'history') {
    // Renders the canonical "Nuestra historia" / CMIC introduction section.
    return (
      <div data-testid={`block-${block.id}`} data-block-kind="history">
        <HistorySection />
      </div>
    );
  }

  return null;
}

export function BlocksRenderer({ blocks, locale, editMode }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <>
      {blocks.map((b) => <BlockRenderer key={b.id} block={b} locale={locale} editMode={editMode} />)}
    </>
  );
}

/* ===== Embedded form / member list helpers ===== */
function ContactFormBlock({ block, locale }) {
  const d = block.data || {};
  const title = d[`title_${locale}`] || d.title_es || (locale === 'en' ? 'Contact us' : 'Contáctanos');
  const subtitle = d[`subtitle_${locale}`] || d.subtitle_es || '';
  return (
    <section data-testid={`block-${block.id}`} data-block-kind="contact_form" className="py-14 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center">{title}</h2>}
        {subtitle && <p className="text-charcoal-600 text-center mt-2 mb-2">{subtitle}</p>}
        <div className="mt-6">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function EmbeddedMemberListBlock({ block, locale }) {
  const d = block.data || {};
  const title = d[`title_${locale}`] || d.title_es || '';
  const limit = parseInt(d.limit, 10) || 12;
  const layout = d.layout || 'grid'; // 'grid' | 'list'
  // Field visibility (defaults: photo + languages on, rest off).
  const showPhoto = d.show_photo === undefined ? true : !!d.show_photo;
  const showLanguages = d.show_languages === undefined ? true : !!d.show_languages;
  const showSpecialties = !!d.show_specialties;
  const showModalities = !!d.show_modalities;
  const showCity = !!d.show_city;
  const randomOrder = !!d.random_order;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get('/interpreters')
      .then(({ data }) => {
        if (!alive) return;
        const arr = Array.isArray(data) ? data : (data?.results || []);
        // Filter by language if specified
        let filtered = arr;
        if (d.filter_language) {
          const lang = d.filter_language.toLowerCase();
          filtered = arr.filter((it) => {
            const asArr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
            const all = [...asArr(it.active_languages), ...asArr(it.passive_languages), ...asArr(it.languages)];
            return all.some((l) => (l || '').toLowerCase().includes(lang));
          });
        }
        if (d.filter_city) {
          const c = d.filter_city.toLowerCase();
          filtered = filtered.filter((it) => (it.city || '').toLowerCase().includes(c));
        }
        // Random order: shuffle a copy (Fisher–Yates) so it changes each load.
        if (randomOrder) {
          filtered = [...filtered];
          for (let i = filtered.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
          }
        }
        setItems(filtered.slice(0, limit));
      })
      .catch(() => setItems([]))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [d.filter_language, d.filter_city, limit, randomOrder]);

  const metaLine = (it) => {
    const toArr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
    const parts = [];
    if (showLanguages) parts.push([...toArr(it.active_languages), ...toArr(it.languages)].join(' · '));
    if (showSpecialties && toArr(it.specialty).length) parts.push(toArr(it.specialty).join(' · '));
    if (showModalities && toArr(it.modalities).length) parts.push(toArr(it.modalities).join(' · '));
    if (showCity && it.city) parts.push(it.city);
    return parts.filter(Boolean).join(' — ') || it.city || '';
  };

  return (
    <section data-testid={`block-${block.id}`} data-block-kind="embedded_member_list" className="py-14 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && <h2 className="text-3xl font-bold text-charcoal-800 text-center mb-8">{title}</h2>}
        {loading ? (
          <p className="text-center text-charcoal-500 text-sm">…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-charcoal-500 text-sm">{locale === 'en' ? 'No members found.' : 'Sin resultados.'}</p>
        ) : (
          <div className={layout === 'list' ? 'divide-y divide-gray-100 max-w-3xl mx-auto' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
            {items.map((it) => (
              <Link key={it.slug || it.id} to={`/${locale}/${locale === 'en' ? 'directory' : 'directorio'}/${it.slug}`} className={layout === 'list' ? 'flex items-center gap-4 py-3 hover:bg-gray-50 px-3 rounded-xl transition-colors' : 'bg-gray-50 hover:bg-cmic-50 transition-colors rounded-2xl p-4 flex items-center gap-4 border border-gray-100'} data-testid={`block-${block.id}-item-${it.slug}`}>
                {showPhoto && (it.photo_url ? (
                  <img src={absoluteUrl(it.photo_url)} alt={it.full_name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-cmic-100 text-cmic-700 flex items-center justify-center font-semibold flex-shrink-0">{(it.full_name || '?').charAt(0)}</div>
                ))}
                <div className="min-w-0">
                  <div className="font-semibold text-charcoal-800 truncate">{it.full_name}</div>
                  <div className="text-xs text-charcoal-500 truncate">{metaLine(it)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {d.show_view_all && (
          <div className="text-center mt-8">
            <Link to={`/${locale}/directorio`} className="inline-flex items-center text-cmic-600 hover:text-cmic-700 font-semibold">
              {locale === 'en' ? 'View full directory' : 'Ver directorio completo'} →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}


/**
 * PresidentMessageBlock — a stylish presidential message section.
 *
 * Renders a portrait (or elegant placeholder), the message body with a large
 * pull-quote glyph, and a signature with name + role.
 *
 * data fields (all bilingual):
 *   - role_es / role_en      → "Mensaje de la Presidenta" / "Message from..."
 *   - body_es / body_en      → HTML (paragraphs)
 *   - name                   → "Mtra. Aída Mendoza"  (single, no translation)
 *   - title_es / title_en    → "Presidenta del CMIC"
 *   - photo_url              → optional uploaded image
 *   - period                 → "Bienio 2026-2028"  (optional)
 */
function PresidentMessageBlock({ block, locale }) {
  const d = block.data || {};
  const role = d[`role_${locale}`] || d.role_es || (locale === 'en' ? 'Message from the President' : 'Mensaje de la Presidencia');
  const html = d[`body_${locale}`] || d.body_es || '';
  const name = d.name || '';
  const title = d[`title_${locale}`] || d.title_es || '';
  const period = d.period || '';
  const photo = d.photo_url ? absoluteUrl(d.photo_url) : '';
  const initials = (name || '').split(/\s+/).filter(Boolean).slice(-2).map((s) => s.charAt(0)).join('').toUpperCase() || 'P';

  return (
    <section
      data-testid={`block-${block.id}`}
      data-block-kind="president_message"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef2f7 50%, #e2eaf3 100%)' }}
    >
      {/* Decorative quote glyph */}
      <div
        aria-hidden="true"
        className="absolute -top-8 left-4 sm:left-16 text-[18rem] sm:text-[24rem] leading-none font-serif select-none pointer-events-none"
        style={{ color: 'rgba(14, 83, 157, 0.06)' }}
      >
        “
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Portrait column */}
        <div className="lg:col-span-4 flex flex-col items-center lg:items-start">
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-cmic-400 to-cmic-700 rounded-3xl opacity-20 blur-2xl" />
            {photo ? (
              <img
                src={photo}
                alt={name || role}
                className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-3xl object-cover shadow-xl ring-4 ring-white"
              />
            ) : (
              <div className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-3xl shadow-xl ring-4 ring-white flex items-center justify-center bg-gradient-to-br from-cmic-500 to-cmic-800 text-white">
                <span className="text-6xl font-bold tracking-wider">{initials}</span>
              </div>
            )}
          </div>
          <div className="mt-6 text-center lg:text-left">
            {name && (
              <p className="text-lg font-bold text-charcoal-800 leading-tight" data-testid="president-name">
                {name}
              </p>
            )}
            {title && (
              <p className="text-sm text-cmic-700 font-semibold mt-1">{title}</p>
            )}
            {period && (
              <p className="text-xs text-charcoal-500 mt-1 tracking-wide uppercase">{period}</p>
            )}
          </div>
        </div>

        {/* Message column */}
        <div className="lg:col-span-8">
          <p className="text-xs font-bold tracking-[0.2em] text-cmic-600 uppercase mb-3">
            {role}
          </p>
          <div className="h-1 w-16 bg-gradient-to-r from-cmic-500 to-cmic-700 rounded-full mb-6" />
          <div
            className="prose prose-lg max-w-none text-charcoal-700 prose-p:leading-relaxed prose-p:text-[1.05rem] prose-strong:text-cmic-700 prose-headings:text-charcoal-900 prose-headings:font-bold"
            dangerouslySetInnerHTML={{ __html: SAFE(html) }}
          />
          {name && (
            <div className="mt-8 pt-6 border-t border-cmic-200/60 flex items-center gap-3">
              <span className="text-3xl text-cmic-500 font-serif italic">—</span>
              <div>
                <p className="text-sm font-bold text-charcoal-800">{name}</p>
                {title && <p className="text-xs text-charcoal-500">{title}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
